/**
 * Electricity prices by geography (California, counties, cities).
 * Source: electricity_prices_by_geography, electricity_utility_crosswalk (Supabase).
 */

import { supabaseClient } from './supabase';

export interface ElectricityPriceRow {
  geofips: string;
  location_name: string | null;
  county: string | null;
  geography_type: string;
  year: number;
  sector: string;
  avg_price_per_kwh: number;
  /** EIA-861 utility name for this row when known (year-varying: IOU before CCA start, CCA after). */
  utility_name?: string | null;
  /** Energy component $/kWh (CCA) when CCA-served; null for bundled. */
  energy_price_per_kwh?: number | null;
  /** Delivery component $/kWh (IOU) when CCA-served; null for bundled. */
  delivery_price_per_kwh?: number | null;
}

export interface ElectricityPricesByGeographyResult {
  location: ElectricityPriceRow[];
  california: ElectricityPriceRow[];
  utilityName: string | null;
  /** IOU name from crosswalk (for Delivery label when CCA+IOU stacked). */
  eia861Iou?: string | null;
  /** CCA name from crosswalk (for Energy label when CCA+IOU stacked). */
  eia861Cca?: string | null;
}

const PRICE_SELECT_COLS =
  'geofips,location_name,county,geography_type,year,sector,avg_price_per_kwh,utility_name,energy_price_per_kwh,delivery_price_per_kwh';

/** Total $/kWh; when CCA energy + IOU delivery are split, use their sum. */
export function effectivePricePerKwh(row: ElectricityPriceRow): number {
  const energy = row.energy_price_per_kwh;
  const delivery = row.delivery_price_per_kwh;
  if (energy != null && delivery != null && Number.isFinite(energy) && Number.isFinite(delivery)) {
    return energy + delivery;
  }
  return row.avg_price_per_kwh;
}

/** All geofips string variants used across population, census place, and legacy price rows. */
export function electricityPriceGeofipsVariants(raw: string | number): string[] {
  const out = new Set<string>();
  const trimmed = String(raw).trim();
  if (!trimmed) return [];

  out.add(trimmed);
  const stripped = trimmed.replace(/^0+/, '') || '0';
  out.add(stripped);
  if (stripped.length <= 2) {
    out.add(stripped.padStart(2, '0'));
  }

  const n = parseInt(stripped, 10);
  if (Number.isFinite(n)) {
    out.add(String(n));
    if (String(n).length === 6 && String(n).startsWith('6')) {
      out.add(`6${'0'}${String(n).slice(1)}`);
    }
    if (String(n).length === 7 && String(n).startsWith('60')) {
      out.add(String(n).slice(0, 2) + String(n).slice(3));
    }
    out.add(String(n).padStart(7, '0'));
  }

  return Array.from(out).filter(Boolean);
}

function normalizePriceRow(row: ElectricityPriceRow): ElectricityPriceRow {
  const total = effectivePricePerKwh(row);
  return total !== row.avg_price_per_kwh ? { ...row, avg_price_per_kwh: total } : row;
}

function rowScore(row: ElectricityPriceRow, canonicalGeofips: string): number {
  let score = 0;
  if (row.utility_name?.trim()) score += 4;
  if (String(row.geofips).replace(/^0+/, '') === canonicalGeofips.replace(/^0+/, '')) score += 2;
  if (row.geography_type && row.geography_type !== 'State') score += 1;
  return score;
}

/** Prefer utility-matched rows over legacy CA-average copies stored under alternate geofips keys. */
function mergeLocationPriceRows(rows: ElectricityPriceRow[], canonicalGeofips: string): ElectricityPriceRow[] {
  const best = new Map<string, ElectricityPriceRow>();
  for (const row of rows) {
    const key = `${row.year}|${row.sector}`;
    const normalized = normalizePriceRow(row);
    const existing = best.get(key);
    if (!existing || rowScore(normalized, canonicalGeofips) > rowScore(existing, canonicalGeofips)) {
      best.set(key, normalized);
    }
  }
  return Array.from(best.values()).sort((a, b) => a.year - b.year || a.sector.localeCompare(b.sector));
}

async function fetchCrosswalkForGeofips(
  variants: string[]
): Promise<{ eia861_utility_name: string | null; eia861_iou: string | null; eia861_cca: string | null } | null> {
  for (const variant of variants) {
    const { data, error } = await supabaseClient
      .from('electricity_utility_crosswalk')
      .select('eia861_utility_name,eia861_iou,eia861_cca')
      .eq('geofips', variant)
      .maybeSingle();
    if (!error && data) return data;
  }
  return null;
}

async function fetchLocationPriceRows(
  variants: string[],
  locationName?: string
): Promise<ElectricityPriceRow[]> {
  const collected: ElectricityPriceRow[] = [];
  for (const variant of variants) {
    const { data, error } = await supabaseClient
      .from('electricity_prices_by_geography')
      .select(PRICE_SELECT_COLS)
      .eq('geofips', variant)
      .order('year');
    if (!error && data?.length) {
      collected.push(...(data as ElectricityPriceRow[]));
    }
  }

  if (!collected.length && locationName?.trim()) {
    const { data, error } = await supabaseClient
      .from('electricity_prices_by_geography')
      .select(PRICE_SELECT_COLS)
      .eq('location_name', locationName.trim())
      .neq('geography_type', 'State')
      .order('year');
    if (!error && data?.length) {
      collected.push(...(data as ElectricityPriceRow[]));
    }
  }

  const canonical = variants[0]?.replace(/^0+/, '') || variants[0] || '';
  return mergeLocationPriceRows(collected, canonical);
}

/**
 * Fetch electricity prices for a location and California by geofips.
 * Also returns the EIA-861 utility name for the location (if mapped in crosswalk).
 */
export async function fetchElectricityPricesByGeography(
  geofips: string | number,
  locationName?: string
): Promise<ElectricityPricesByGeographyResult> {
  const raw = geofips !== null && geofips !== undefined && String(geofips).trim() !== '' ? geofips : null;
  if (raw === null) {
    const caRes = await supabaseClient
      .from('electricity_prices_by_geography')
      .select(PRICE_SELECT_COLS)
      .eq('geofips', '06')
      .order('year');
    return {
      location: [],
      california: ((caRes.data || []) as ElectricityPriceRow[]).map(normalizePriceRow),
      utilityName: null,
      eia861Iou: null,
      eia861Cca: null,
    };
  }

  const stripped = String(raw).trim().replace(/^0+/, '') || '0';
  const geofipsNorm = stripped.length <= 2 ? stripped.padStart(2, '0') : stripped;
  const variants = electricityPriceGeofipsVariants(raw);

  const [location, caRes, cwRes] = await Promise.all([
    fetchLocationPriceRows(variants, locationName),
    supabaseClient.from('electricity_prices_by_geography').select(PRICE_SELECT_COLS).eq('geofips', '06').order('year'),
    fetchCrosswalkForGeofips(variants),
  ]);

  if (caRes.error) throw new Error(caRes.error.message || 'Failed to fetch California electricity prices');

  const california = ((caRes.data || []) as ElectricityPriceRow[]).map(normalizePriceRow);

  return {
    location,
    california,
    utilityName: cwRes?.eia861_utility_name ?? null,
    eia861Iou: cwRes?.eia861_iou ?? null,
    eia861Cca: cwRes?.eia861_cca ?? null,
  };
}

/** Latest year's average $/kWh; optionally filter to one sector (e.g. Residential). */
export function getLatestAveragePrice(rows: ElectricityPriceRow[], sector?: string): number | null {
  const filtered = sector ? rows.filter((r) => r.sector === sector) : rows;
  if (!filtered.length) return null;
  const years = Array.from(new Set(filtered.map((r) => r.year))).sort((a, b) => b - a);
  const latestYear = years[0];
  const byYear = filtered.filter((r) => r.year === latestYear);
  if (!byYear.length) return null;
  return byYear.reduce((s, r) => s + effectivePricePerKwh(r), 0) / byYear.length;
}

/**
 * Building electricity price $/kWh for cost models.
 * When resFractionKwh is provided (0–1), uses weighted average: res * resFractionKwh + com * (1 - resFractionKwh).
 * Otherwise uses simple average of residential and commercial when both available.
 */
export function getBuildingElectricityPricePerKwh(
  residential: number | null,
  commercial: number | null,
  resFractionKwh?: number | null
): number | null {
  if (residential != null && commercial != null) {
    let price: number;
    if (resFractionKwh != null && resFractionKwh >= 0 && resFractionKwh <= 1) {
      price = residential * resFractionKwh + commercial * (1 - resFractionKwh);
    } else {
      price = (residential + commercial) / 2;
    }
    return Math.round(price * 1000) / 1000;
  }
  return residential ?? commercial ?? null;
}

/**
 * Fetch latest-year residential and commercial electricity consumption (kWh) for a geography.
 * Used to weight building electricity price by res/com share in most recent year.
 */
export async function fetchLatestResCommercialKwhByGeofips(geofips: string | number): Promise<{
  resKwh: number;
  comKwh: number;
  year: number;
} | null> {
  const variants = electricityPriceGeofipsVariants(geofips);
  if (!variants.length) return null;
  try {
    const collectedRes: { year: number; total_kwh: number }[] = [];
    const collectedCom: { year: number; total_kwh: number }[] = [];
    for (const variant of variants) {
      const [resRes, comRes] = await Promise.all([
        supabaseClient
          .from('elec_res_format')
          .select('year, total_kwh')
          .eq('geofips', variant)
          .eq('sector', 'Residential')
          .order('year', { ascending: false }),
        supabaseClient
          .from('elec_com_format')
          .select('year, total_kwh')
          .eq('geofips', variant)
          .eq('sector', 'Commercial')
          .order('year', { ascending: false }),
      ]);
      if (resRes.data?.length) collectedRes.push(...(resRes.data as { year: number; total_kwh: number }[]));
      if (comRes.data?.length) collectedCom.push(...(comRes.data as { year: number; total_kwh: number }[]));
    }
    if (!collectedRes.length || !collectedCom.length) return null;

    const resByYear = new Map<number, number>();
    for (const r of collectedRes) {
      const y = r.year;
      resByYear.set(y, (resByYear.get(y) ?? 0) + (Number(r.total_kwh) || 0));
    }
    const comByYear = new Map<number, number>();
    for (const r of collectedCom) {
      const y = r.year;
      comByYear.set(y, (comByYear.get(y) ?? 0) + (Number(r.total_kwh) || 0));
    }
    const allYears = Array.from(new Set([...Array.from(resByYear.keys()), ...Array.from(comByYear.keys())])).sort(
      (a, b) => b - a
    );
    for (const year of allYears) {
      const resKwh = resByYear.get(year) ?? 0;
      const comKwh = comByYear.get(year) ?? 0;
      if (resKwh > 0 || comKwh > 0) {
        return { resKwh, comKwh, year };
      }
    }
    return null;
  } catch {
    return null;
  }
}

/** Get latest year's residential and commercial prices for a location (for Policy tool). */
export async function fetchLatestElectricityPricesForLocation(
  geofips: string | number,
  locationName?: string
): Promise<{
  residential: number | null;
  commercial: number | null;
  average: number | null;
  /** Building cost assumption: weighted average by res/com kWh share in most recent year; falls back to (res+com)/2. */
  buildingPricePerKwh: number | null;
  utilityName: string | null;
  year: number | null;
}> {
  const [{ location, utilityName }, kwhLatest] = await Promise.all([
    fetchElectricityPricesByGeography(geofips, locationName),
    fetchLatestResCommercialKwhByGeofips(geofips),
  ]);
  const sectors = location.filter((r) => ['Residential', 'Commercial'].includes(r.sector));
  const years = Array.from(new Set(sectors.map((r) => r.year))).sort((a, b) => b - a);
  const latestYear = years[0] ?? null;
  if (latestYear == null) {
    return { residential: null, commercial: null, average: null, buildingPricePerKwh: null, utilityName, year: null };
  }
  const bySector = sectors.filter((r) => r.year === latestYear);
  const resRow = bySector.find((r) => r.sector === 'Residential');
  const comRow = bySector.find((r) => r.sector === 'Commercial');
  const res = resRow ? effectivePricePerKwh(resRow) : null;
  const com = comRow ? effectivePricePerKwh(comRow) : null;
  const allSectors = location.filter((r) => r.year === latestYear);
  const avg =
    allSectors.length > 0
      ? allSectors.reduce((s, r) => s + effectivePricePerKwh(r), 0) / allSectors.length
      : null;
  const totalKwh = kwhLatest ? kwhLatest.resKwh + kwhLatest.comKwh : 0;
  const resFractionKwh = totalKwh > 0 && kwhLatest ? kwhLatest.resKwh / totalKwh : undefined;
  const buildingPricePerKwh = getBuildingElectricityPricePerKwh(res, com, resFractionKwh);
  return {
    residential: res ?? null,
    commercial: com ?? null,
    average: avg ?? buildingPricePerKwh ?? res ?? com,
    buildingPricePerKwh,
    utilityName,
    year: latestYear,
  };
}
